import { z } from 'zod';

/**
 * VISUAL CONTRACTS — atomic, checkable visual assertions authored per page
 * alongside the page prose (issues #845 / #844 / #843 / #786).
 *
 * The SAME object drives image generation, image review, repair and the eval
 * suites. That is the whole point: a free-form "check the picture matches the
 * text" instruction is what shipped the two motivating defects, whereas a
 * decomposition into one-claim-at-a-time questions catches them (TIFA / DSG /
 * VQAScore prior art).
 *
 * Two real defects from the evidence book explain the field list:
 *
 * - **The direction defect.** The prose says the hero FILLED a jug; the
 *   illustration shows him EMPTYING it. Both pictures contain "hero + jug +
 *   water", so an assertion that only names entities passes. Only an assertion
 *   that names the {@link VisualAssertion.verb} AND its
 *   {@link VisualAssertion.direction} catches the inversion. That is what
 *   those two optional fields exist for — they are not decoration.
 * - **The recurring-prop defect.** One prop was rendered as a different object
 *   on five pages. {@link VisualContract.canonRefs} names the canonical object
 *   ids a page depends on, which is what lets the reference stack attach the
 *   page where each object was ESTABLISHED.
 *
 * WIRE POSTURE: every string here is a permissive `z.string()`, never a
 * `z.enum`. Contracts are authored by an LLM and consumed by a reviewer whose
 * vocabulary the server will keep extending (CLAUDE.md DB/wire schema rule).
 * The one closed set — {@link VisualAssertionVerdict} — is narrowed by a
 * typeguard AFTER parsing, exactly like `isReviewFlag` /
 * `isDocumentedContinuityFlag` do for their flag vocabularies.
 */

/**
 * One atomic visual claim about a page, checkable from the picture alone.
 *
 * A good assertion is falsifiable by looking: "the jug is being FILLED from
 * the pool". A bad one merely lists what is present ("the boy and the jug are
 * visible") — that passes on the inverted image too.
 */
export const visualAssertionSchema = z.object({
  /**
   * Stable within its page. The reviewer CITES this id when returning a
   * verdict, so it is the join key between the contract and
   * {@link VisualAssertionResult}.
   */
  id: z.string(),
  /** The claim itself, phrased so a picture can satisfy or violate it. */
  claim: z.string(),
  /** Who/what performs the action — a character or canonical object name. */
  subject: z.string().optional(),
  /** The action the prose asserts, e.g. `fill` (NOT `pour`). */
  verb: z.string().optional(),
  /**
   * The direction that verb has, e.g. `into the jug` — the guard against a
   * picture that shows the same actors performing the INVERSE action.
   */
  direction: z.string().optional(),
});

export type VisualAssertion = z.infer<typeof visualAssertionSchema>;

/** The per-page contract: what the illustration must (and must not) show. */
export const visualContractSchema = z.object({
  /**
   * The page this contract belongs to. Deliberately a plain `z.string()` and
   * NOT `pageIdSchema` — this arrives inside an LLM response and a contract
   * with an odd id must degrade to "no contract for that page", never abort
   * the parse of an otherwise-good page batch.
   */
  pageId: z.string(),
  /**
   * The assertions the illustration must satisfy.
   *
   * An EMPTY array is a legal shape but a meaningless contract — it reads as
   * "nothing to check" and would silently pass every page. Callers must route
   * on {@link isCheckableContract} rather than on presence.
   */
  mustShow: z.array(visualAssertionSchema),
  /** Free-form things that must be ABSENT (e.g. a prop the page has not met). */
  mustNotShow: z.array(z.string()).optional(),
  /**
   * Canonical object ids this page depends on. Drives first-appearance
   * reference selection — the page where each object was established is
   * attached to both the generation and the review call.
   */
  canonRefs: z.array(z.string()),
});

export type VisualContract = z.infer<typeof visualContractSchema>;

/**
 * The three verdicts OUR code routes on. A CLOSED set — but the value arrives
 * from an LLM, so the wire stays `z.string()` (see
 * {@link visualAssertionResultSchema}) and narrowing happens here.
 *
 * - `satisfied` — the picture supports the claim.
 * - `violated`  — the picture contradicts it. Maps to the existing
 *   `text-image-mismatch` continuity slug; no new flag vocabulary.
 * - `cannot-tell` — the reviewer cannot see enough to judge (occlusion, crop).
 *   This NEVER sets a flag: describe the obstruction, flag nothing (#826).
 */
export const VISUAL_ASSERTION_VERDICTS = [
  'satisfied',
  'violated',
  'cannot-tell',
] as const;

export type VisualAssertionVerdict = (typeof VISUAL_ASSERTION_VERDICTS)[number];

const VISUAL_ASSERTION_VERDICT_SET = new Set<string>(VISUAL_ASSERTION_VERDICTS);

/** True when `value` is one of the three verdicts our code routes on. */
export function isVisualAssertionVerdict(
  value: string
): value is VisualAssertionVerdict {
  return VISUAL_ASSERTION_VERDICT_SET.has(value);
}

/**
 * Narrow an arbitrary wire value to a verdict, degrading anything
 * unrecognised to `cannot-tell`.
 *
 * `cannot-tell` is the only safe default: it is the one verdict that never
 * sets a flag, so a verdict we could not understand can never manufacture a
 * `text-image-mismatch` on a healthy page. The opposite default would let a
 * malformed judge response spend repair budget.
 */
export function toVisualAssertionVerdict(
  value: unknown
): VisualAssertionVerdict {
  return typeof value === 'string' && isVisualAssertionVerdict(value)
    ? value
    : 'cannot-tell';
}

/**
 * One per-assertion result the reviewer returns (the plan calls this shape
 * `AssertionVerdict`; renamed here so it cannot be confused with the verdict
 * STRING union {@link VisualAssertionVerdict}).
 */
export const visualAssertionResultSchema = z.object({
  /** Must match a {@link VisualAssertion.id} on the page's contract. */
  assertionId: z.string(),
  /**
   * Permissive on the wire on purpose — narrow with
   * {@link toVisualAssertionVerdict}. A judge that invents a fourth word must
   * degrade to `cannot-tell`, not fail the whole review parse.
   */
  verdict: z.string(),
  /**
   * What the reviewer actually sees, quoted. Optional because a judge that
   * omits its justification should still yield a usable verdict rather than
   * dropping the result entirely.
   */
  observed: z.string().optional(),
});

export type VisualAssertionResult = z.infer<typeof visualAssertionResultSchema>;

/**
 * True when the contract has at least one assertion to check.
 *
 * A contract lost on resume must degrade to "no contract" (skip the per-page
 * assertions), NEVER to an empty one — `mustShow: []` reads as "nothing to
 * check" and would silently pass every page.
 */
export function isCheckableContract(contract: VisualContract): boolean {
  return contract.mustShow.length > 0;
}

/**
 * Tolerant parse: returns the contract, or `null` for anything malformed.
 *
 * Contracts are best-effort enrichment — a page without one still generates
 * and still reviews. So every ingestion point (page-gen envelope, transcript
 * scrape, resume) uses this instead of `.parse()`: a bad contract degrades to
 * "no contract" and can never throw its way into a failed book.
 */
export function parseVisualContract(value: unknown): VisualContract | null {
  const result = visualContractSchema.safeParse(value);
  return result.success ? result.data : null;
}
